import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";

interface Project {
  _id: string;
  title: string;
  description: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  age: string;
  category: string;
  platform: string;
  bannerImage: string;
  isActive: boolean;
}

const HighCode: React.FC = () => {
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch projects from API
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3030'}/highcode/projects`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch projects');
        }
        
        const data = await response.json();
        setProjects(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load projects');
        console.error('Error fetching projects:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Array of available banner images
  const bannerImages = [
    '/images/projects/astropi-ndvi-banner.png',
    '/images/projects/blockscad-bug-banner.png',
    '/images/projects/butterfly-banner.png',
    '/images/projects/cats-banner.png',
    '/images/projects/cats-vs-dogs-banner.png',
    '/images/projects/cd-sebento-htmlcss-1-banner.png',
    '/images/projects/cd-sebento-htmlcss-2-banner.png',
    '/images/projects/cd-sebento-htmlcss-3-banner.png',
    '/images/projects/count-creatures-banner.png',
    '/images/projects/cress-egg-heads-banner.png',
    '/images/projects/decision-tree-banner.png',
    '/images/projects/grow-dragonfly-banner.png',
    '/images/projects/grow-tree-introduction.png',
    '/images/projects/infrared-bird-box-banner.png',
    '/images/projects/led-firefly-banner.png',
    '/images/projects/nature-rover-banner.png',
    '/images/projects/popular-pets-banner.png',
    '/images/projects/sense-hat-data-logger-banner.png',
    '/images/projects/serene-scene-banner.png',
    '/images/projects/sprite-needs-you-banner.png',
    '/images/projects/sunrise-banner.png',
    '/images/projects/swarms-schools-flocks-banner.png',
    '/images/projects/tree-life-simulator-banner.png',
    '/images/projects/turtle-race-banner.png',
    '/images/projects/turtle-snowflakes-banner.png',
    '/images/projects/turtle-tracker-banner.png',
    '/images/projects/welcome-antarctica-banner.png',
    '/images/projects/world-environment-day-banner.png'
  ];

  // Function to get a banner image based on project index
  const getBannerImage = (index: number) => {
    return bannerImages[index % bannerImages.length];
  };

  // Show loading spinner while fetching
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Show error if projects failed to load
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Failed to Load Projects</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const filteredProjects = selectedFilter === "All" 
    ? projects 
    : projects.filter(project => project.difficulty === selectedFilter);


  const handleStarterPackClick = (packType: string) => {
    // Scroll to starter packs section
    const starterPacksSection = document.getElementById('starter-packs');
    if (starterPacksSection) {
      starterPacksSection.scrollIntoView({ behavior: 'smooth' });
    }
    
    // You could add additional logic here like:
    // - Track analytics
    // - Show a notification
    // - Pre-select a specific pack
    console.log(`User interested in ${packType} starter pack`);
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-red-500">
      {/* Hero Section */}
      <section className="relative py-20 px-6 text-center overflow-hidden min-h-screen flex items-center">
        {/* Background Video */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
            style={{ zIndex: 1 }}
            onError={(e) => {
              console.error('Video failed to load:', e);
              console.log('Video src:', '/videos/kids_hero_section.mp4');
            }}
            onLoadStart={() => console.log('Video loading started')}
            onCanPlay={() => console.log('Video can play')}
            onLoadedData={() => console.log('Video data loaded')}
            preload="metadata"
          >
            <source src="/videos/kids_hero_section.mp4" type="video/mp4" />
            {/* Fallback for browsers that don't support video */}
            Your browser does not support the video tag.
          </video>
          
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600/60 via-pink-500/60 to-red-500/60" style={{ zIndex: 2 }}></div>
        </div>
        
        {/* Floating shapes background */}
        <div className="absolute inset-0 overflow-hidden z-10">
          <div className="absolute top-10 left-10 w-20 h-20 bg-yellow-300 rounded-full animate-bounce opacity-70"></div>
          <div className="absolute top-32 right-20 w-16 h-16 bg-green-300 rounded-full animate-pulse opacity-60"></div>
          <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-blue-300 rounded-full animate-bounce delay-1000 opacity-80"></div>
          <div className="absolute top-1/2 right-1/3 w-14 h-14 bg-orange-300 rounded-full animate-ping opacity-60"></div>
          <div className="absolute bottom-32 right-10 w-18 h-18 bg-pink-300 rounded-full animate-bounce delay-500 opacity-70"></div>
          {/* Additional floating elements for more visual interest */}
          <div className="absolute top-1/4 left-1/3 w-8 h-8 bg-cyan-300 rounded-full animate-bounce delay-300 opacity-80"></div>
          <div className="absolute bottom-1/3 right-1/4 w-10 h-10 bg-lime-300 rounded-full animate-pulse delay-700 opacity-70"></div>
          <div className="absolute top-3/4 left-1/5 w-6 h-6 bg-rose-300 rounded-full animate-ping delay-500 opacity-90"></div>
        </div>
        
        <div className="relative z-20">
          <h1 className="text-6xl md:text-8xl font-bold text-white mb-6 transform hover:scale-105 transition-transform duration-300 drop-shadow-2xl" style={{fontFamily: 'Lexend, sans-serif'}}>
            HIGH CODE
          </h1>
          <p className="text-2xl md:text-3xl text-white mb-8 font-semibold drop-shadow-lg" style={{fontFamily: 'Lexend, sans-serif'}}>
            STEM Adventures for Young Innovators!
          </p>
          <p className="text-lg md:text-xl text-white/95 mb-12 max-w-4xl mx-auto drop-shadow-md" style={{fontFamily: 'Lexend, sans-serif'}}>
            Join our amazing journey into the world of electronics, coding, and robotics! 
            Build 50+ cool projects with Arduino, Raspberry Pi, ESP boards, and more!
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/start-building"
              className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-4 px-8 rounded-full text-xl transform hover:scale-110 transition-all duration-300 shadow-2xl hover:shadow-3xl backdrop-blur-sm border-2 border-yellow-300 inline-block"
              style={{fontFamily: 'Lexend, sans-serif'}}
            >
              Start Building Now!
            </Link>
            <Link
              to="/all-projects"
              className="bg-green-400 hover:bg-green-500 text-white font-bold py-4 px-8 rounded-full text-xl transform hover:scale-110 transition-all duration-300 shadow-2xl hover:shadow-3xl backdrop-blur-sm border-2 border-green-300 inline-block"
              style={{fontFamily: 'Lexend, sans-serif'}}
            >
              View Projects
            </Link>
            <Link
              to="/high-code/games"
              className="bg-purple-400 hover:bg-purple-500 text-white font-bold py-4 px-8 rounded-full text-xl transform hover:scale-110 transition-all duration-300 shadow-2xl hover:shadow-3xl backdrop-blur-sm border-2 border-purple-300 inline-block"
              style={{fontFamily: 'Lexend, sans-serif'}}
            >
              🎮 Play Games
            </Link>
          </div>
          
          {/* Additional animated elements for more visual interest */}
          <div className="absolute -top-20 -left-20 w-40 h-40 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full opacity-20 animate-pulse"></div>
          <div className="absolute -bottom-20 -right-20 w-32 h-32 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full opacity-20 animate-bounce"></div>
        </div>
      </section>

      {/* Age Groups Section */}
      <section className="py-16 px-6 bg-white relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 bg-blue-500 rounded-full animate-pulse"></div>
          <div className="absolute top-20 right-20 w-24 h-24 bg-green-500 rounded-full animate-bounce delay-1000"></div>
          <div className="absolute bottom-20 left-1/4 w-28 h-28 bg-purple-500 rounded-full animate-ping delay-500"></div>
        </div>
        <div className="container mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12 text-gray-800">
            Perfect for Every Age!
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-pink-400 to-purple-500 rounded-3xl p-8 text-white transform hover:scale-105 transition-transform duration-300 shadow-xl">
              <div className="text-6xl mb-4">🧸</div>
              <h3 className="text-2xl font-bold mb-4">Little Builders (4-8)</h3>
              <p className="text-lg">
                Simple, colorful projects with big buttons and fun sounds! 
                Learn basic concepts through play and exploration.
              </p>
              <ul className="mt-4 space-y-2">
                <li>✨ LED light shows</li>
                <li>🎵 Musical instruments</li>
                <li>🤖 Simple robots</li>
                <li>🌈 Color mixing games</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-400 to-cyan-500 rounded-3xl p-8 text-white transform hover:scale-105 transition-transform duration-300 shadow-xl">
              <div className="text-6xl mb-4">🚀</div>
              <h3 className="text-2xl font-bold mb-4">Young Engineers (9-13)</h3>
              <p className="text-lg">
                More complex projects that teach real engineering concepts! 
                Build games, sensors, and interactive devices.
              </p>
              <ul className="mt-4 space-y-2">
                <li>🎮 Video game controllers</li>
                <li>🌡️ Weather stations</li>
                <li>🏠 Smart home devices</li>
                <li>🚗 Remote control cars</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-green-400 to-teal-500 rounded-3xl p-8 text-white transform hover:scale-105 transition-transform duration-300 shadow-xl">
              <div className="text-6xl mb-4">⚡</div>
              <h3 className="text-2xl font-bold mb-4">Teen Innovators (14-18)</h3>
              <p className="text-lg">
                Advanced projects that prepare you for college and careers! 
                Build real-world applications and IoT devices.
              </p>
              <ul className="mt-4 space-y-2">
                <li>📱 Mobile app integration</li>
                <li>🌐 Internet of Things</li>
                <li>🤖 AI-powered robots</li>
                <li>🔬 Scientific instruments</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Starter Packs Section */}
      <section id="starter-packs" className="py-16 px-6 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500">
        <div className="container mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12 text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
            Amazing Starter Packs!
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Arduino Pack */}
            <div 
              className="rounded-3xl p-8 shadow-2xl transform hover:scale-105 transition-transform duration-300 relative overflow-hidden"
              style={{
                backgroundImage: 'url(/kids/arduino_pack.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            >
              {/* Enhanced overlay for text readability */}
              <div className="absolute inset-0 bg-gradient-to-br from-green-900/80 via-black/70 to-emerald-900/80 rounded-3xl"></div>
              <div className="relative z-10">
                <div className="text-center mb-6">
                  <h3 className="text-3xl font-bold text-white mb-4" style={{fontFamily: 'Lexend, sans-serif'}}>Arduino Adventure Pack</h3>
                <p className="text-lg text-white/90" style={{fontFamily: 'Lexend, sans-serif'}}>
                  Everything you need to start your Arduino journey!
                </p>
              </div>
              
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">✅</span>
                    <span className="text-lg text-white" style={{fontFamily: 'Lexend, sans-serif'}}>Arduino Uno R3</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">✅</span>
                    <span className="text-lg text-white" style={{fontFamily: 'Lexend, sans-serif'}}>LEDs, resistors, and wires</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">✅</span>
                    <span className="text-lg text-white" style={{fontFamily: 'Lexend, sans-serif'}}>Sensors (temperature, light, sound)</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">✅</span>
                    <span className="text-lg text-white" style={{fontFamily: 'Lexend, sans-serif'}}>Motors and servos</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">✅</span>
                    <span className="text-lg text-white" style={{fontFamily: 'Lexend, sans-serif'}}>Breadboard and jumper wires</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">✅</span>
                    <span className="text-lg text-white" style={{fontFamily: 'Lexend, sans-serif'}}>Step-by-step project guide</span>
                  </div>
                </div>
                
                <div className="mt-8 text-center">
                  <div className="text-3xl font-bold text-white mb-2" style={{fontFamily: 'Lexend, sans-serif'}}>₵1,200</div>
                  <button 
                    onClick={() => handleStarterPackClick('Arduino')}
                    className="bg-white hover:bg-gray-100 text-green-600 font-bold py-3 px-6 rounded-full text-lg transform hover:scale-110 transition-all duration-300"
                    style={{fontFamily: 'Lexend, sans-serif'}}
                  >
                    🛒 Get This Pack!
                  </button>
                </div>
              </div>
            </div>

            {/* Raspberry Pi Pack */}
            <div 
              className="rounded-3xl p-8 shadow-2xl transform hover:scale-105 transition-transform duration-300 relative overflow-hidden"
              style={{
                backgroundImage: 'url(/kids/rasberry_pack.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            >
              {/* Enhanced overlay for text readability */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-black/70 to-cyan-900/80 rounded-3xl"></div>
              <div className="relative z-10">
                <div className="text-center mb-6">
                  <h3 className="text-3xl font-bold text-white mb-4" style={{fontFamily: 'Lexend, sans-serif'}}>Raspberry Pi Explorer Pack</h3>
                <p className="text-lg text-white/90" style={{fontFamily: 'Lexend, sans-serif'}}>
                  Discover the power of Raspberry Pi computing!
                </p>
              </div>
              
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">✅</span>
                    <span className="text-lg text-white" style={{fontFamily: 'Lexend, sans-serif'}}>Raspberry Pi 4 Model B</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">✅</span>
                    <span className="text-lg text-white" style={{fontFamily: 'Lexend, sans-serif'}}>MicroSD card with OS</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">✅</span>
                    <span className="text-lg text-white" style={{fontFamily: 'Lexend, sans-serif'}}>GPIO expansion board</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">✅</span>
                    <span className="text-lg text-white" style={{fontFamily: 'Lexend, sans-serif'}}>Camera module</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">✅</span>
                    <span className="text-lg text-white" style={{fontFamily: 'Lexend, sans-serif'}}>Touch screen display</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">✅</span>
                    <span className="text-lg text-white" style={{fontFamily: 'Lexend, sans-serif'}}>Python programming guide</span>
                  </div>
                </div>
                
                <div className="mt-8 text-center">
                  <div className="text-3xl font-bold text-white mb-2" style={{fontFamily: 'Lexend, sans-serif'}}>₵2,000</div>
                  <button 
                    onClick={() => handleStarterPackClick('Raspberry Pi')}
                    className="bg-white hover:bg-gray-100 text-blue-600 font-bold py-3 px-6 rounded-full text-lg transform hover:scale-110 transition-all duration-300"
                    style={{fontFamily: 'Lexend, sans-serif'}}
                  >
                    🛒 Get This Pack!
                  </button>
                </div>
              </div>
            </div>

            {/* ESP32 Pack */}
            <div 
              className="rounded-3xl p-8 shadow-2xl transform hover:scale-105 transition-transform duration-300 relative overflow-hidden"
              style={{
                backgroundImage: 'url(/kids/esp32_pack.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            >
              {/* Enhanced overlay for text readability */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-900/80 via-black/70 to-pink-900/80 rounded-3xl"></div>
              <div className="relative z-10">
                <div className="text-center mb-6">
                  <h3 className="text-3xl font-bold text-white mb-4" style={{fontFamily: 'Lexend, sans-serif'}}>ESP32 IoT Explorer Pack</h3>
                <p className="text-lg text-white/90" style={{fontFamily: 'Lexend, sans-serif'}}>
                  Build connected devices and IoT projects!
                </p>
              </div>
              
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">✅</span>
                    <span className="text-lg text-white" style={{fontFamily: 'Lexend, sans-serif'}}>ESP32 Development Board</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">✅</span>
                    <span className="text-lg text-white" style={{fontFamily: 'Lexend, sans-serif'}}>WiFi & Bluetooth modules</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">✅</span>
                    <span className="text-lg text-white" style={{fontFamily: 'Lexend, sans-serif'}}>Sensors (DHT22, ultrasonic, PIR)</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">✅</span>
                    <span className="text-lg text-white" style={{fontFamily: 'Lexend, sans-serif'}}>OLED display & LEDs</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">✅</span>
                    <span className="text-lg text-white" style={{fontFamily: 'Lexend, sans-serif'}}>Breadboard & jumper wires</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">✅</span>
                    <span className="text-lg text-white" style={{fontFamily: 'Lexend, sans-serif'}}>IoT project tutorials</span>
                  </div>
                </div>
                
                <div className="mt-8 text-center">
                  <div className="text-3xl font-bold text-white mb-2" style={{fontFamily: 'Lexend, sans-serif'}}>₵1,500</div>
                  <button 
                    onClick={() => handleStarterPackClick('ESP32')}
                    className="bg-white hover:bg-gray-100 text-purple-600 font-bold py-3 px-6 rounded-full text-lg transform hover:scale-110 transition-all duration-300"
                    style={{fontFamily: 'Lexend, sans-serif'}}
                  >
                    🛒 Get This Pack!
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Examples Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              50+ Amazing Projects!
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-4">
              From simple LED projects to advanced robotics - there's something for every skill level!
            </p>
            <p className="text-sm text-gray-500">
              Showing {filteredProjects.length} {selectedFilter === "All" ? "projects" : `${selectedFilter.toLowerCase()} projects`}
            </p>
          </div>
          
          {/* Difficulty Filter Tabs */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-full p-2 shadow-lg">
              <button 
                onClick={() => setSelectedFilter("All")}
                className={`px-6 py-2 rounded-full font-semibold mr-2 transition-all duration-300 ${
                  selectedFilter === "All" 
                    ? "bg-green-100 text-green-800" 
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                All Projects
              </button>
              <button 
                onClick={() => setSelectedFilter("Easy")}
                className={`px-6 py-2 rounded-full font-semibold mr-2 transition-all duration-300 ${
                  selectedFilter === "Easy" 
                    ? "bg-green-100 text-green-800" 
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                Easy
              </button>
              <button 
                onClick={() => setSelectedFilter("Medium")}
                className={`px-6 py-2 rounded-full font-semibold mr-2 transition-all duration-300 ${
                  selectedFilter === "Medium" 
                    ? "bg-yellow-100 text-yellow-800" 
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                Medium
              </button>
              <button 
                onClick={() => setSelectedFilter("Hard")}
                className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${
                  selectedFilter === "Hard" 
                    ? "bg-red-100 text-red-800" 
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                Hard
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden">
                {/* Top Graphical Section */}
                <div className="h-48 relative overflow-hidden">
                  <img 
                    src={getBannerImage(index)} 
                    alt={`${project.title} project illustration`}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Bottom Text Section */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-black" style={{fontFamily: 'Lexend, sans-serif'}}>
                      ☐☐☐ {project.platform} - Level {project.difficulty === 'Easy' ? '1' : project.difficulty === 'Medium' ? '2' : '3'}
                    </span>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                      project.category === 'LEDs' ? 'bg-blue-100 text-blue-800' :
                      project.category === 'Games' ? 'bg-green-100 text-green-800' :
                      project.category === 'Robotics' ? 'bg-purple-100 text-purple-800' :
                      project.category === 'Sensors' ? 'bg-yellow-100 text-yellow-800' :
                      project.category === 'Music' ? 'bg-pink-100 text-pink-800' :
                      project.category === 'IoT' ? 'bg-orange-100 text-orange-800' :
                      project.category === 'Mobile' ? 'bg-indigo-100 text-indigo-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {project.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-black mb-2 underline" style={{fontFamily: 'Lexend, sans-serif'}}>
                    {project.title}
                  </h3>
                  <p className="text-sm text-black mb-4" style={{fontFamily: 'Lexend, sans-serif'}}>
                    {project.description}
                  </p>
                  <Link 
                    to={`/project/${project._id}`}
                    className="flex items-center space-x-2 text-sm font-bold text-black hover:text-gray-600 transition-colors" 
                    style={{fontFamily: 'Lexend, sans-serif'}}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>View Project</span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
          
          {/* View All Projects Button */}
          <div className="text-center mt-12">
            <Link
              to="/all-projects"
              className="inline-block bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-8 rounded-full text-lg transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              View All 50+ Projects
            </Link>
          </div>
        </div>
      </section>

      {/* Learning Path Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500">
        <div className="container mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12 text-white">
            Your Learning Journey!
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: "1", title: "Start Simple", desc: "Learn basics with fun projects", emoji: "🌟" },
              { step: "2", title: "Build Skills", desc: "Create more complex devices", emoji: "⚡" },
              { step: "3", title: "Code & Connect", desc: "Add programming and internet", emoji: "🌐" },
              { step: "4", title: "Invent & Share", desc: "Create your own projects!", emoji: "🚀" }
            ].map((step, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 text-center transform hover:scale-105 transition-transform duration-300 shadow-xl">
                <div className="text-4xl mb-3">{step.emoji}</div>
                <div className="text-3xl font-bold text-purple-600 mb-2">{step.step}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-green-400 to-blue-500">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
            Ready to Start Your STEM Adventure?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
            Join thousands of young innovators who are already building the future! 
            Start with any project that excites you and learn at your own pace.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/start-building"
              className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-4 px-8 rounded-full text-xl transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl inline-block"
            >
              Choose Your Starter Pack
            </Link>
            <Link
              to="/all-projects"
              className="bg-white hover:bg-gray-100 text-blue-600 font-bold py-4 px-8 rounded-full text-xl transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl inline-block"
            >
              Browse All Projects
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HighCode;
